import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { acAPI } from "../../lib/academicApi";
import { PageHeader, Loading, Section, StatusPill, EmptyState } from "../../components/Common";
import {
  BookOpen, Clock, ClipboardCheck, AlertTriangle, ArrowLeft, CalendarClock, Users, ArrowRight, GraduationCap,
} from "lucide-react";

export default function TeacherWorkbench() {
  const { school } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { acAPI.workbench().then(({ data }) => setData(data)).catch(() => setData({})); }, []);
  if (!data) return <Loading label="Loading Teacher Workbench…" />;

  const cards = [
    { label: "Today's lessons", value: data.today_lessons?.length || 0, icon: CalendarClock, testId: "wb-lessons" },
    { label: "Homework active", value: data.homework_active?.length || 0, icon: BookOpen, testId: "wb-homework" },
    { label: "Assessments upcoming", value: data.assessments_upcoming?.length || 0, icon: Clock, testId: "wb-assess" },
    { label: "Moderation pending", value: data.moderation_pending || 0, icon: ClipboardCheck, testId: "wb-moderation" },
    { label: "Interventions open", value: data.interventions_open || 0, icon: AlertTriangle, testId: "wb-interventions" },
  ];

  return (
    <div data-testid="teacher-workbench">
      <PageHeader
        kicker="Academic360™ · Teacher Workbench"
        title="Today at a glance"
        subtitle="Your lessons, homework, marking and moderation in one place."
        actions={<button onClick={() => nav(`/education360/${school}/academic`)} className="hl-button-secondary text-xs"><ArrowLeft className="w-3 h-3 inline mr-1" />Academic Hub</button>}
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        {cards.map(c => (
          <div key={c.label} className="hl-card p-4" data-testid={c.testId}>
            <div className="flex items-center gap-2 text-slate-500 text-[11px] uppercase tracking-widest font-semibold"><c.icon className="w-3.5 h-3.5" />{c.label}</div>
            <div className="font-display font-bold text-3xl text-[color:var(--hl-navy)] mt-1">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Today's lessons">
          <div className="hl-card p-4" data-testid="lessons-list">
            {(data.today_lessons || []).length === 0 && <EmptyState title="No lessons scheduled" body="Plan lessons in Curriculum Studio." />}
            {(data.today_lessons || []).map(l => (
              <div key={l.id} className="flex items-center justify-between py-2 border-b border-slate-100 text-sm">
                <div>
                  <div className="text-xs text-slate-500 mono">{l.scheduled_at?.slice(11, 16)}</div>
                  <div className="font-semibold text-slate-800">{l.title}</div>
                  <div className="text-xs text-slate-500">{l.duration_minutes}m</div>
                </div>
                <StatusPill status={l.status} />
              </div>
            ))}
          </div>
        </Section>

        <Section title="Upcoming assessments" subtitle="Click to open the mark book">
          <div className="hl-card p-4" data-testid="upcoming-assessments">
            {(data.assessments_upcoming || []).length === 0 && <EmptyState title="Nothing upcoming" body="Schedule an assessment from the Mark Books page." />}
            {(data.assessments_upcoming || []).map(a => (
              <button
                key={a.id}
                onClick={() => nav(`/education360/${school}/academic/markbook/${a.id}`)}
                className="w-full flex items-center justify-between py-2 border-b border-slate-100 text-sm text-left hover:bg-slate-50 px-1"
                data-testid={`upcoming-${a.id}`}
              >
                <div>
                  <div className="font-semibold text-slate-800">{a.title}</div>
                  <div className="text-xs text-slate-500">{a.type} · {a.scheduled_at?.slice(0, 10)}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </button>
            ))}
          </div>
        </Section>

        <Section title="Homework in flight">
          <div className="hl-card p-4" data-testid="homework-list">
            {(data.homework_active || []).length === 0 && <EmptyState title="No active homework" body="Publish a new set from Homework360." />}
            {(data.homework_active || []).map(h => (
              <button
                key={h.id}
                onClick={() => nav(`/education360/${school}/academic/homework/${h.id}`)}
                className="w-full flex items-center justify-between py-2 border-b border-slate-100 text-sm text-left hover:bg-slate-50 px-1"
              >
                <div>
                  <div className="font-semibold text-slate-800">{h.title}</div>
                  <div className="text-xs text-slate-500">Due {h.due_at?.slice(0, 10)}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </button>
            ))}
          </div>
        </Section>

        <Section title="Quick jumps">
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: "Mark Books", to: `/education360/${school}/academic/markbooks`, icon: GraduationCap },
              { label: "Homework360", to: `/education360/${school}/academic/homework`, icon: BookOpen },
              { label: "Moderation", to: `/education360/${school}/academic/moderation`, icon: ClipboardCheck },
              { label: "Grade Books", to: `/education360/${school}/academic/gradebook`, icon: Users },
            ].map(q => (
              <button key={q.label} onClick={() => nav(q.to)} className="hl-card p-4 flex items-center gap-3 text-sm hover:border-[color:var(--hl-orange)]">
                <q.icon className="w-4 h-4 text-slate-500" />
                <span className="flex-1 text-left font-semibold text-slate-800">{q.label}</span>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </button>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}
